import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { Assignment } from '../../../../models/assignment.school.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { SubmittedAssignment } from '../../../../models/submit.assignment.school.model';
import { UserModel } from '../../../../models/user.model';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolAssignmentService } from '../../../../services/assignment.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as assignmentActions from '../../../../state/actions/assignment.school.actions';
import { AppState } from '../../../../state/appstate';
import * as assignmentSelector from '../../../../state/selectors/assignment.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { RichTextEditorComponent } from '../../../components/quill-editor/rich-text-editor/rich-text-editor.component';
import { DlgSubmitAssignmentComponent } from '../dlg-submit-assignment/dlg-submit-assignment.component';

@Component({
  selector: 'app-detail-assignment',
  standalone: true,
  imports: [
    AsyncPipe,
    BtnActionComponent,
    BtnFabComponent,
    BtnBackComponent,
    RichTextEditorComponent,
    MomentPipe,
  ],
  templateUrl: './detail-assignment.component.html',
  styleUrl: './detail-assignment.component.css',
})
export class DetailAssignmentComponent {
  route: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  utilsService: UtilsService = inject(UtilsService);
  private dialog: MatDialog = inject(MatDialog);
  assignmentService: SchoolAssignmentService = inject(SchoolAssignmentService);

  htmlData: string = '';

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  studentId: string | null = this.route.snapshot.paramMap.get('studentId');
  assignmentId: string | null =
    this.route.snapshot.paramMap.get('assignmentId');
  academicYear: number | undefined = this.route.snapshot.queryParamMap.get(
    'academicYear',
  )
    ? Number(this.route.snapshot.queryParamMap.get('academicYear'))
    : undefined;

  user$: Observable<UserModel | null> = new Observable();
  student: SchoolMember | null | undefined = null;
  assignment: Assignment | null | undefined;
  studentwork: Observable<SubmittedAssignment[]> = new Observable();

  myRoles$: Observable<any> = new Observable();
  loading$: Observable<boolean> = new Observable();
  loadStudentWork$: Observable<boolean> = new Observable();

  readonly: boolean = true;

  working: boolean = false;

  exit$: Subject<void> = new Subject<void>();

  private params$ = combineLatest({
    params: this.route.paramMap,
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => {
      let pMap = params;
      let currentRoute = this.route;

      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
        studentId: pMap.get('studentId'),
        assignmentId: pMap.get('assignmentId'),
        academicYear: queryParams.get('academicYear')
          ? Number(queryParams.get('academicYear'))
          : undefined,
      };
    }),
    takeUntil(this.exit$),
  );

  constructor() {
    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.user$ = this.store.select((state) => state.user.user);
  }

  ngOnInit() {
    this.params$.subscribe(
      ({ groupId, studentId, assignmentId, academicYear }) => {
        this.groupId = groupId;
        this.studentId = studentId;
        this.assignmentId = assignmentId;
        this.academicYear = academicYear;

        this.logger.debug(
          `DetailAssignmentComponent :: Received route params groupId=${groupId}, studentId=${studentId}, assignmentId=${assignmentId}, academicYear=${academicYear}`,
        );

        if (groupId) {
          this.loadAssignment(groupId, studentId, assignmentId, academicYear);
        }

        this.myRoles$ = this.store
          .select(groupSelector.getMyRolesInGroup(this.groupId))
          .pipe(takeUntil(this.exit$));
      },
    );
  }

  loadAssignment(
    groupId: string,
    studentId: string | null,
    assignmentId: string | null,
    academicYear: number | undefined,
  ) {
    this.store
      .select(groupSelector.getGroupById(groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        if (!group) return;
        this.store
          .select(
            assignmentSelector.getAssignmentById({
              groupId: groupId,
              assignmentId: assignmentId,
              academicYear: academicYear,
            }),
          )
          .pipe(takeUntil(this.exit$))
          .subscribe(async (assignment) => {
            this.logger.debug(
              `DetailAssignmentComponent :: Fetched assignment from store: ${JSON.stringify(assignment)}`,
            );
            if (assignment == undefined || assignment == null) {
              this.store.dispatch(
                assignmentActions.getAssignment({
                  groupId: groupId,
                  assignmentId: assignmentId!,
                }),
              );
              return;
            }
            if (this.assignment?.modified == assignment?.modified) {
              return;
            }
            this.assignment = assignment;
            try {
              this.loading$ = of(true);
              this.assignmentService
                .getAssignmentDocument(assignment?.assignmentUrl!)
                .then(async (data: any) => {
                  this.htmlData = JSON.stringify(data);
                  this.loading$ = of(false);
                })
                .catch((error: any) => {
                  this.logger.log(
                    `DetailAssignmentComponent :: Error fetching notes: ${JSON.stringify(error)}`,
                  );
                  this.toastr.error(
                    `Error occurred while fetching assignment from our servers. ${error?.message}. Please try again later.`,
                    'Error fetching Assignment',
                  );
                  this.loading$ = of(false);
                });
            } catch (error: any) {
              this.logger.error(
                `DetailAssignmentComponent :: Error fetching assignment notes: ${error}`,
              );
              this.toastr.error(
                `Error occurred while fetching assignment notes. ${error?.message}. Please try again later.`,
                'Error Fetching Assignment',
              );
              this.loading$ = of(false);
              return;
            }
          });
      });

    this.store
      .select(memberSelector.getSchoolMemberById(this.studentId, this.groupId!))
      .pipe(takeUntil(this.exit$))
      .subscribe((student) => {
        this.student = student;
      });

    this.loadStudentWork();
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  async loadStudentWork() {
    if (!this.groupId || !this.assignmentId || !this.studentId) return;
    try {
      this.studentwork = await this.assignmentService.getSubmittedAssignments({
        groupId: this.groupId,
        assignmentId: this.assignmentId,
        studentId: this.studentId,
      });
    } catch (error) {
      this.toastr.error(
        'Error occurred while fetching submitted work. Please try again later.',
        'Error fetching Submitted Work',
      );
      return;
    }
  }

  onSubmitAssignment() {
    if (!this.groupId || !this.assignmentId) return;

    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      groupId: this.groupId,
      academicYear: this.assignment?.academicYear,
      assignmentId: this.assignmentId,
      gradeId: this.assignment?.gradeId,
      studentId: this.studentId,
      studentName: this.student?.name,
    };
    const dialogRef = this.dialog.open(
      DlgSubmitAssignmentComponent,
      dialogConfig,
    );
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Assignment submitted successfully');
        this.loadStudentWork();
      }
    });
  }

  onDelete(id: string) {
    // this.utilsService.onDeleteConfirmation({
    //   title: 'Confirm Delete',
    //   description:
    //     'Are you sure you want to delete this submitted assignment? This action cannot be undone.',
    // })
    //   .pipe(take(1))
    //   .subscribe((confirmed) => {
    //     if (confirmed) {
    //       this.store.dispatch(
    //         assignmentActions.deleteAction({
    //           groupId: this.groupId!,
    //           assignmentId: this.assignmentId!,
    //           submissionId: id,
    //         })
    //       );
    //     }
    //   });
  }
}
